import { Box, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useReducer } from "react";
import * as React from "react";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useParams,
} from "react-router";

import { useFileTransfer } from "@/providers/FileTransfer";
import { AlertDialog } from "@/shared/components";
import { IngestAsAssetDialog } from "@/shared/components/visualization/IngestAsAssetDialog";
import { useRecentlyCompletedInvocations } from "@/shared/domain/actions/hooks/";
import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type FileRecord } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import {
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import {
  fileQueryKeys,
  useDeleteDirectoryMutation,
  useDeleteFileMutation,
  useDirectoryContents,
  useDirectoryExtensions,
  useDirectoryItemCount,
  useFilesAtPaths,
} from "@/shared/domain/files/hooks";
import { encodePath } from "@/shared/navigation";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import { FileBrowserActionTypes, FileBrowserAction } from "./actions";
import { DragAndDropDisplay } from "./DragAndDropDisplay";
import {
  type Crumb,
  FileBrowserBreadcrumbs,
  FileBrowserViewControls,
} from "./FileBrowserBreadcrumbs";
import { FileBrowserDndContext } from "./FileBrowserDndContext";
import { FileBrowserOperationBar } from "./FileBrowserOperationBar";
import { computeExtensionsArray } from "./helper";
import { type InheritedFiles, InheritedFilesContext } from "./inheritedFiles";
import { fileBrowserReducer } from "./reducer";
import { FileBrowserState } from "./state";
import { useSearchTerm } from "./useSearchTerm";
import { useSortParams } from "./useSortParams";

const sliderValueFormat = (value: number) => {
  return `Zoom: ${value}`;
};

interface AssociationFileBrowserProps {
  /** The dataset, org, device or device type whose files are shown. */
  associationId: string;
  /** The org that owns the files: the resource owner on reads, and where uploads are recorded. */
  orgId: string | undefined;
  /**
   * URL of the root folder. A subfolder's URL is this plus `/` and its path,
   * so the route that renders the browser must end in `/*`. Unused when
   * `folderSearchParam` is given.
   */
  rootUrl: string;
  /**
   * Name of a URL search parameter that holds the open folder's path, in
   * place of the route's `*` segment. A page that shows two browsers gives
   * one of them this, so that each has its own open folder.
   */
  folderSearchParam?: string;
  /** The root folder's crumb label, such as the dataset ID or "Assets". */
  rootLabel: string;
  /** Crumbs before the root folder's, such as the page that lists datasets. */
  parentCrumbs: Crumb[];
  /**
   * The `dv_` association of a device, when the files shown are a device
   * type's as that device inherits them. The browser is then read-only, has
   * no search box of its own (it follows the URL's search and sort, which the
   * device's own browser sets), and marks each file the device overrides with
   * an entry of its own at the same path.
   */
  inheritedBy?: string;
}

/** The kinds of association whose robot descriptions can be ingested as assets. */
const INGESTABLE_ROOT_TYPES: ReadonlySet<string> = new Set([
  AssociationType.Dataset,
  AssociationType.Device,
  AssociationType.DeviceType,
]);

/**
 * A folder browser over the files one dataset, org, device or device type
 * holds: the directory listing with breadcrumbs, sort and filter controls,
 * New Folder, Upload, rename, delete and selection. The folder shown comes
 * from the URL's `*` segment under `rootUrl`, or from the search parameter
 * `folderSearchParam` names.
 *
 * A dataset also gets Visualize, Add to collection, sessions, Invoke Action,
 * trigger status, the image and video grids, the file type filter, ingestion
 * labels and an exact item count. Orgs, devices and device types lack the
 * routes or concepts those need. Without a search term their folders have no
 * item count, so the pager shows "of more than N" and moves by next page token.
 */
export const AssociationFileBrowser: React.FC<AssociationFileBrowserProps> = ({
  associationId,
  orgId,
  rootUrl,
  folderSearchParam,
  rootLabel,
  parentCrumbs,
  inheritedBy,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDataset =
    fileAssociationType(associationId) === AssociationType.Dataset;
  const { sortColumn, sortDirection } = useSortParams();
  const { "*": pathSegment } = useParams();
  const rawFilePath =
    folderSearchParam === undefined
      ? pathSegment
      : searchParams.get(folderSearchParam);

  // Trim whitespace and strip leading/trailing slashes so concatenating
  // this value into navigation URLs and API requests cannot produce
  // double slashes or empty path segments.
  const filePath = rawFilePath?.trim()?.replace(/^\/+|\/+$/g, "") ?? "";

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { beginFileUpload } = useFileTransfer();

  const [fileBrowserState, dispatch] = useReducer<
    React.Reducer<FileBrowserState, FileBrowserAction>
  >(fileBrowserReducer, {
    alert: {
      isOpen: false,
      title: "",
      text: "",
      action: async () => {},
    },
    activeToggle: "all",
    // Inherited files always open in the table, which marks the overridden
    // ones; the URL's `display` parameter belongs to the device's own browser.
    displayMode:
      inheritedBy === undefined
        ? (searchParams.get("display") as string) ?? "table"
        : "table",
    error: null,
    fileTypes: [],
    gridItems: 3,
    selectedItems: [],
    showHiddenFiles: false,
    tableConfig: {
      page: 0,
      rowsPerPage: 25,
      pageTokens: { 0: undefined },
    },
  });

  const [searchTerm, setSearchTerm] = useSearchTerm();

  // Hoisted so there is one wizard for the whole browser rather than one per
  // row, matching how the confirmation dialog is arranged. The file it
  // holds is both the subject and the open/closed flag.
  const [ingestingFile, setIngestingFile] = React.useState<FileRecord | null>(
    null,
  );

  // The one place the ingest release gate is read. Withholding the handler,
  // rather than drilling a boolean through the five components between here and
  // the row menu, is what enforces it: the prop is optional the rest of the way
  // down, so TypeScript makes every call site account for its absence, and the
  // natural handling is the fall-through each already has (View shows the raw
  // file, a double click or tile click opens the file view).
  //
  // Nothing about *viewing* is gated. A workspace that already references a
  // generated asset keeps rendering it; this only stops new ones being made.
  //
  // Ingest as Asset's converter resolves a robot description's meshes from
  // the association the description is in. It is offered in a dataset and in
  // a device's or device type's configurations, but not in the org's assets
  // or in a listing of inherited files, which is read-only.
  const canIngestAssets =
    useCheckFeatureFlag("release-3d-assets") &&
    INGESTABLE_ROOT_TYPES.has(fileAssociationType(associationId)) &&
    inheritedBy === undefined;
  // Only a dataset's files can be ingested, so only a dataset labels them.
  const showIngestionLabels =
    useCheckFeatureFlag("release-ingestion-labels") && isDataset;

  const currentToken =
    fileBrowserState.tableConfig?.pageTokens[fileBrowserState.tableConfig.page];

  const deleteFileMutation = useDeleteFileMutation();
  const deleteDirectoryMutation = useDeleteDirectoryMutation();

  const directoryExtensionsQuery = useDirectoryExtensions(
    filePath ?? "",
    associationId,
  );

  const directoryContentsQuery = useDirectoryContents(
    filePath ?? "",
    associationId,
    orgId,
    fileBrowserState.tableConfig.rowsPerPage,
    currentToken,
    searchTerm,
    fileBrowserState.showHiddenFiles,
    fileBrowserState.fileTypes,
    sortColumn,
    sortDirection,
  );

  const currentPage = directoryContentsQuery.data?.items;
  const nextToken = directoryContentsQuery.data?.nextToken;

  const currentPagePaths = React.useMemo(
    () =>
      inheritedBy === undefined
        ? []
        : (currentPage ?? [])
            .filter(isFile)
            .map((node) => node.file.relative_path),
    [currentPage, inheritedBy],
  );
  // The device's own files at the paths on this page; each one overrides the
  // device type's file at the same path.
  const overridingFilesQuery = useFilesAtPaths(
    inheritedBy,
    currentPagePaths,
    orgId,
  );
  const inheritedFiles = React.useMemo<InheritedFiles | null>(
    () =>
      inheritedBy === undefined
        ? null
        : { overriddenPaths: new Set(overridingFilesQuery.data?.keys()) },
    [inheritedBy, overridingFilesQuery.data],
  );
  React.useEffect(() => {
    if (directoryContentsQuery.error) {
      dispatch({
        type: FileBrowserActionTypes.SetError,
        payload: directoryContentsQuery.error,
      });
    }
  }, [directoryContentsQuery.error]);

  const itemCountQuery = useDirectoryItemCount(
    filePath ?? "",
    associationId,
    orgId,
    searchTerm,
    fileBrowserState.fileTypes,
    fileBrowserState.showHiddenFiles,
  );

  // `null` for an org, device or device type folder with no search term, which
  // the service cannot count; the pager then moves by next page token alone.
  const itemCount = itemCountQuery.data ?? undefined;

  const extensions = computeExtensionsArray(directoryExtensionsQuery.data);

  /**
   * URL that opens `folderPath` (empty for the root folder) in this browser
   * and leaves the rest of the URL's search parameters as they are, among
   * them the folder open in another browser on the page.
   */
  const folderUrl = (folderPath: string): string => {
    if (folderSearchParam === undefined) {
      const folder = folderPath
        ? `${rootUrl}/${encodePath(folderPath)}`
        : rootUrl;
      return `${folder}?${searchParams.toString()}`;
    }
    const withFolder = new URLSearchParams(searchParams);
    if (folderPath) {
      withFolder.set(folderSearchParam, folderPath);
    } else {
      withFolder.delete(folderSearchParam);
    }
    return `${pathname}?${withFolder.toString()}`;
  };

  const handleRowDoubleClick = (row: FileSystemNode) => {
    if (isDirectory(row)) {
      const folderPath = `${filePath ? filePath + "/" : ""}${row.name}`;
      void navigate(folderUrl(folderPath));
    }
  };

  // Back to the first page when the folder, search, sort or hidden-files
  // toggle changes, since the stored page tokens are valid only for the
  // listing that issued them. Search and sort live in the URL, so another
  // browser on the page (a device's own, above its inherited files) can change
  // them too.
  useEffect(() => {
    dispatch({
      type: FileBrowserActionTypes.SetTablePageToZero,
      payload: null,
    });
  }, [
    filePath,
    searchTerm,
    sortColumn,
    sortDirection,
    fileBrowserState.showHiddenFiles,
  ]);

  const filePrefix = filePath;

  const handleGridSliderChange = (gridItems: number) => {
    dispatch({
      type: FileBrowserActionTypes.SetGridItems,
      payload: gridItems,
    });
  };

  function handleActionConfirmation(
    title: string,
    text: string,
    action: () => Promise<void>,
  ) {
    dispatch({
      type: FileBrowserActionTypes.SetAlertState,
      payload: {
        isOpen: true,
        title: title,
        text: text,
        action,
      },
    });
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!orgId) {
      return;
    }

    await beginFileUpload(event, orgId, associationId, filePrefix);
    await directoryContentsQuery.refetch();
  };

  function summarizeItems(items: FileSystemNode[]): string {
    const hasFiles = items.some(isFile);
    const hasDirectories = items.some(isDirectory);

    if (items.length > 1) {
      if (hasFiles && hasDirectories) {
        // Mixed case, using a general term
        return "Items";
      }
      return hasFiles ? "Files" : "Directories";
    }

    return isFile(items[0]) ? "File" : "Directory";
  }

  const selectedFiles = React.useMemo(() => {
    return fileBrowserState.selectedItems
      .filter(isFile)
      .map((item) => item.file);
  }, [fileBrowserState.selectedItems]);

  const handleSort = (column: SortColumn, direction: SortDirection) => {
    // Update URL parameters to reflect sort state
    setSearchParams(
      (oldParams) => {
        const params = new URLSearchParams(oldParams);

        params.set("sortColumn", column);
        params.set("sortDirection", direction);

        return params;
      },
      { replace: true },
    );
  };

  return (
    <InheritedFilesContext.Provider value={inheritedFiles}>
      <div>
        <Box
          sx={{
            display: "flex",
            mt: theme.spacing(0),
            mb: theme.spacing(1.5),
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: theme.spacing(1),
          }}
        >
          <FileBrowserOperationBar
            associationId={associationId}
            extensions={extensions}
            fileTypes={fileBrowserState.fileTypes}
            display={fileBrowserState.displayMode}
            activeToggle={fileBrowserState.activeToggle}
            handleViewToggle={(display, fileTypes, activeToggle) => {
              dispatch({
                type: FileBrowserActionTypes.HandleViewToggle,
                payload: { display, fileTypes, activeToggle },
              });
              setSearchParams(
                (oldParams) => {
                  const params = new URLSearchParams(oldParams);
                  params.set("display", display);
                  return params;
                },
                { replace: true },
              );
            }}
            handleDeleteItems={(items) => {
              if (items.length === 0) {
                return;
              }

              const deleteTerm = summarizeItems(items);
              const deleteTitle = `Delete ${deleteTerm}`;
              handleActionConfirmation(
                `${deleteTitle}`,
                `Are you sure you want to delete ${items.length} ${deleteTerm.toLowerCase()}?`,
                async function () {
                  try {
                    const deletePromises = [];

                    const directoryPaths = items
                      .filter(isDirectory)
                      .map((item) => item.directory.relative_path);

                    if (directoryPaths.length > 0) {
                      deletePromises.push(
                        deleteDirectoryMutation.mutateAsync({
                          associationId,
                          directoryPaths,
                        }),
                      );
                    }

                    const fileDeletePromises = items
                      .filter(isFile)
                      .map((item) => {
                        return deleteFileMutation.mutateAsync({
                          file: item.file,
                          resourceOwnerId: item.file.org_id,
                        });
                      });

                    deletePromises.push(...fileDeletePromises);

                    await Promise.all(deletePromises);

                    dispatch({
                      type: FileBrowserActionTypes.DeleteFiles,
                      payload: items,
                    });
                  } catch (e) {
                    ErrorMonitoringService.captureError(e);
                    dispatch({
                      type: FileBrowserActionTypes.SetError,
                      payload: e instanceof Error ? e : new Error(String(e)),
                    });
                  } finally {
                    // Whatever failed, some of the items may be gone, so the
                    // listing is refetched either way.
                    await directoryContentsQuery.refetch();
                    await itemCountQuery.refetch();
                  }
                },
              );
            }}
            handleDeselectAll={() => {
              dispatch({
                type: FileBrowserActionTypes.SetSelectedItems,
                payload: [],
              });
            }}
            handleFileTypeSelection={(fileTypes) => {
              dispatch({
                type: FileBrowserActionTypes.SetFileTypes,
                payload: fileTypes,
              });
            }}
            handleFileSearch={(searchTerm) => {
              setSearchTerm(searchTerm);
              dispatch({
                type: FileBrowserActionTypes.SetSearchTerm,
                payload: searchTerm,
              });
            }}
            handleShowHiddenFilesToggle={() => {
              dispatch({
                type: FileBrowserActionTypes.SetShowHiddenFiles,
                payload: !fileBrowserState.showHiddenFiles,
              });
            }}
            showHiddenFiles={fileBrowserState.showHiddenFiles}
            uploadButtonPressed={() => fileInputRef?.current?.click()}
            selectedItems={fileBrowserState.selectedItems}
          />
          <FileBrowserViewControls
            associationId={associationId}
            displayMode={fileBrowserState.displayMode}
            gridItems={fileBrowserState.gridItems}
            handleDisplayToggle={(mode) => {
              if (mode !== null) {
                dispatch({
                  type: FileBrowserActionTypes.SetDisplayMode,
                  payload: mode,
                });
                setSearchParams(
                  (oldParams) => {
                    const params = new URLSearchParams(oldParams);
                    params.set("display", mode);
                    return params;
                  },
                  { replace: true },
                );
              }
            }}
            handleGridSliderChange={handleGridSliderChange}
            sliderValueFormat={sliderValueFormat}
          />
        </Box>
        <div>
          <FileBrowserDndContext
            associationId={associationId}
            dispatch={dispatch}
            filePath={filePath}
            selectedItems={fileBrowserState.selectedItems}
          >
            <FileBrowserBreadcrumbs
              parentCrumbs={parentCrumbs}
              rootLabel={rootLabel}
              folderUrl={folderUrl}
              filePaths={filePath}
            />
            <DragAndDropDisplay
              currentPage={currentPage}
              associationId={associationId}
              displayMode={fileBrowserState.displayMode}
              error={fileBrowserState.error}
              fileInputRef={fileInputRef}
              filePrefix={filePrefix}
              gridItems={fileBrowserState.gridItems}
              handleDeleteRow={(row) => {
                void directoryContentsQuery.refetch();

                dispatch({
                  type: FileBrowserActionTypes.DeleteFiles,
                  payload: [row],
                });
              }}
              handleRowDoubleClick={handleRowDoubleClick}
              handleSelectRows={(
                newRows: FileSystemNode[],
                isSelected: boolean,
              ) => {
                const selectedItemsMap: Map<string, FileSystemNode> = new Map();

                const getKey = (node: FileSystemNode) => {
                  if (isFile(node)) {
                    return `${node.file.file_id}`;
                  } else if (isDirectory(node)) {
                    return `${node.directory.relative_path}`;
                  }
                  return `${node.type}${node.name}`;
                };

                fileBrowserState.selectedItems.forEach((item) => {
                  selectedItemsMap.set(getKey(item), item);
                });

                if (isSelected) {
                  newRows.forEach((row) =>
                    selectedItemsMap.set(getKey(row), row),
                  );
                } else {
                  newRows.forEach((row) =>
                    selectedItemsMap.delete(getKey(row)),
                  );
                }

                const newItems = [...selectedItemsMap.values()];

                dispatch({
                  type: FileBrowserActionTypes.SetSelectedItems,
                  payload: newItems,
                });
              }}
              isPending={directoryContentsQuery.isPending}
              isFetching={directoryContentsQuery.isFetching}
              isPlaceholderData={directoryContentsQuery.isPlaceholderData}
              currentPageFetchedAt={directoryContentsQuery.dataUpdatedAt}
              isSearching={Boolean(
                searchTerm || fileBrowserState.fileTypes.length > 0,
              )}
              onClearError={() => {
                dispatch({
                  type: FileBrowserActionTypes.SetError,
                  payload: null,
                });
              }}
              onChangeInput={handleFileUpload}
              onDropComplete={async () => {
                await directoryContentsQuery.refetch();
                await itemCountQuery.refetch();
              }}
              onPageChange={(_event, newPage) => {
                if (newPage > fileBrowserState.tableConfig.page) {
                  dispatch({
                    type: FileBrowserActionTypes.IncrementTablePage,
                    payload: { newPage: newPage, nextToken: nextToken },
                  });
                } else {
                  dispatch({
                    type: FileBrowserActionTypes.DecrementTablePage,
                    payload: { newPage: newPage },
                  });
                }
              }}
              onRowsPerPageChange={(event) => {
                dispatch({
                  type: FileBrowserActionTypes.UpdateTableRowsPerPage,
                  payload: parseInt(event?.target.value || "", 10) as
                    | 10
                    | 25
                    | 50,
                });
              }}
              selectedItems={fileBrowserState.selectedItems}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              tableConfig={fileBrowserState.tableConfig}
              totalItemCount={itemCount}
              hasNextPage={nextToken !== undefined}
              handleActionConfirmation={handleActionConfirmation}
              onIngestAsAsset={canIngestAssets ? setIngestingFile : undefined}
              showIngestionLabels={showIngestionLabels}
            />
          </FileBrowserDndContext>
        </div>
        <AlertDialog
          dialogOpen={fileBrowserState.alert.isOpen}
          handleClose={() => {
            dispatch({
              type: FileBrowserActionTypes.SetAlertState,
              payload: {
                isOpen: false,
                title: "",
                text: "",
                action: async () => {},
              },
            });
          }}
          dialogTitle={fileBrowserState.alert.title}
          dialogText={fileBrowserState.alert.text}
          dialogAction={fileBrowserState.alert.action}
        />
        <IngestAsAssetDialog
          file={ingestingFile}
          onClose={() => setIngestingFile(null)}
        />
        {isDataset && (
          <DatasetEffects
            datasetId={associationId}
            selectedFiles={selectedFiles}
          />
        )}
      </div>
    </InheritedFilesContext.Provider>
  );
};

/**
 * What the browser does only in a dataset: tells the chat AI which files are
 * selected, and refreshes the listing when an action on the dataset finishes.
 * Hooks cannot be called conditionally, so they live in a component the
 * browser renders only for a dataset.
 */
function DatasetEffects({
  datasetId,
  selectedFiles,
}: {
  datasetId: string;
  selectedFiles: FileRecord[];
}) {
  useRegisterViewingContext("dataset", { selectedFiles });
  useInvalidateDirectoryOnActionCompletion(datasetId);
  return null;
}

function useInvalidateDirectoryOnActionCompletion(datasetId: string) {
  const queryClient = useQueryClient();
  const recentlyCompletedQuery = useRecentlyCompletedInvocations(datasetId);

  const seenInvocationIdsRef = React.useRef<Set<string>>(new Set());

  const completedInvocationIds = React.useMemo(() => {
    return new Set(
      recentlyCompletedQuery.data?.items.map(
        (record) => record.invocation_id,
      ) ?? [],
    );
  }, [recentlyCompletedQuery.data?.items]);

  React.useEffect(() => {
    const seenIds = seenInvocationIdsRef.current;
    const newCompletions = [...completedInvocationIds].filter(
      (id) => !seenIds.has(id),
    );

    // If we found new completions, invalidate cache
    if (newCompletions.length > 0) {
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.directoryContents({ associationId: datasetId }),
        exact: false,
      });
    }

    // Update seen IDs (keep only current ones to prevent memory leaks)
    seenInvocationIdsRef.current = new Set(completedInvocationIds);
  }, [datasetId, completedInvocationIds, queryClient]);
}
